import { ResourceTier, AsteroidSize, RaceId } from '../types/entities.js';
import { hash, SeededRandom } from './hash.js';
import { MASTER_SEED, SECTOR_SIZE, ZONE_TIER_OUTER_RADII, RESOURCE_INFO, RACE_INFO, RACE_RESOURCES, ALL_RESOURCE_TIERS, ALL_RACE_IDS, sectorHasWarpGate, sectorDifficultyMultiplier, sectorDifficultyTier, isPremiumPlanet, } from '../constants/game.js';
import { CIVILIZATION_LORE } from '../constants/civilizations.js';
const STAR_COLORS = [0xffffcc, 0xffddaa, 0xffaa66, 0xff6644, 0xaaccff, 0xffffff];
const PLANET_COLORS = [
    0x445588, 0x884422, 0x228844, 0x886644, 0xcccccc,
    0x664488, 0x448866, 0x888844, 0xcc8844, 0x446688,
];
const ATMOSPHERE_COLORS = [0x4488ff, 0x88ccff, 0xff8844, 0x44ff88, 0xccccff, 0xff4488];
const STATION_PREFIXES = ['Alpha', 'Beta', 'Gamma', 'Delta', 'Omega', 'Nova', 'Nexus', 'Arc'];
const STATION_SUFFIXES = ['Station', 'Outpost', 'Hub', 'Port', 'Dock', 'Beacon', 'Bastion'];
// Word banks for {@link sectorName}. Three curated, non-overlapping lists
// so a deterministic three-word pick feels like a real star-catalog name
// (`Cygnus Drift Prime`, `Vega Veil Bastion`, …) rather than a Mad-Libs
// roll. ~47 × 32 × 23 ≈ 34k unique combinations — more than enough to
// keep the early-game exploration radius effectively collision-free.
const SECTOR_NAME_PREFIXES = [
    'Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta', 'Eta', 'Theta',
    'Iota', 'Kappa', 'Lambda', 'Mu', 'Nu', 'Xi', 'Omicron', 'Pi', 'Rho',
    'Sigma', 'Tau', 'Phi', 'Chi', 'Psi', 'Omega',
    'Nova', 'Orion', 'Vega', 'Lyra', 'Draco', 'Helios', 'Atlas', 'Pegasus',
    'Corvus', 'Polaris', 'Aldebaran', 'Rigel', 'Cygnus', 'Phoenix',
    'Arcturus', 'Antares', 'Spica', 'Centauri', 'Ursa', 'Sirius', 'Kepler',
    'Andromeda', 'Pleiades', 'Cassiopeia',
];
const SECTOR_NAME_MIDDLES = [
    'Void', 'Drift', 'Reach', 'Expanse', 'Veil', 'Spiral', 'Abyss', 'Rift',
    'Horizon', 'Nebula', 'Cradle', 'Crown', 'Fringe', 'Halo', 'Echo',
    'Ember', 'Tide', 'Hollow', 'Storm', 'Frontier', 'Corridor', 'Silence',
    'Requiem', 'Dominion', 'Deep', 'Dusk', 'Dawn', 'Glass', 'Iron',
    'Whisper', 'Shroud', 'Cascade',
];
const SECTOR_NAME_SUFFIXES = [
    'Prime', 'Ascendant', 'Cluster', 'Passage', 'Sanctum', 'Waypoint',
    'Basin', 'Vigil', 'Bastion', 'Heart', 'Threshold', 'Vault', 'Relay',
    'Archive', 'Obelisk', 'Plateau', 'Summit', 'Gulf', 'Ridge', 'Gate',
    'Halo', 'Anchor', 'Beacon',
];
export function sectorDistance(coord) {
    return Math.sqrt(coord.x * coord.x + coord.y * coord.y + coord.z * coord.z);
}
/**
 * Deterministic hostile/neutral/friendly tally for a sector. Reuses the
 * authoritative `generateSector()` pipeline so the numbers shown on the
 * map are exactly what the server will spawn — no second source of truth.
 *
 * Cheap enough to call from a UI selection handler: `generateSector` is
 * pure, has no IO, and runs in well under a millisecond on the inner
 * sectors where most clicks happen. Deeper sectors take a bit longer
 * but stay well below a frame.
 */
export function summarizeSectorNPCs(coord) {
    const sector = generateSector(coord.x, coord.y, coord.z);
    const summary = {
        hostile: 0, neutral: 0, friendly: 0, unknown: 0, total: 0,
    };
    for (const npc of sector.npcs) {
        summary.total++;
        switch (npc.disposition) {
            case 0:
                summary.hostile++;
                break;
            case 1:
                summary.neutral++;
                break;
            case 2:
                summary.friendly++;
                break;
            case 3:
                summary.unknown++;
                break;
            default:
                summary.unknown++;
                break;
        }
    }
    return summary;
}
/**
 * Open-space risk tier (1 = inner bubble … 9 = beyond) from distance to
 * origin. There is **no** tier 0: sectors are never "safe" by grid
 * position alone. Actual safety is **only** the station / warp-gate
 * orbit bubbles (`STATION_SAFE_RADIUS` / `WARP_GATE_SAFE_RADIUS`), which
 * the HUD applies separately from this value.
 *
 * The radii live in `ZONE_TIER_OUTER_RADII` so the entire ladder can be
 * tweaked from one place. The last entry is `Infinity` so any distance
 * lands somewhere on the ladder — the deepest band ("Beyond") has no
 * upper bound by design, which is what makes the universe feel infinite
 * to level-1000+ pilots.
 */
export function getZoneDifficulty(coord) {
    const dist = sectorDistance(coord);
    for (let i = 0; i < ZONE_TIER_OUTER_RADII.length; i++) {
        if (dist <= ZONE_TIER_OUTER_RADII[i])
            return i + 1;
    }
    return ZONE_TIER_OUTER_RADII.length;
}
/**
 * Deterministic cool-sounding name for a sector coordinate, e.g.
 * `Cygnus Drift Prime`, `Vega Veil Bastion`. Three independent word-bank
 * rolls seeded off the same master hash used for generation, so the name
 * is stable across clients, servers and restarts without persistence.
 *
 * The `(0,0,0)` origin gets a deliberately distinct, fixed name so new
 * players always land in the same recognisable "home" sector regardless
 * of where the generator rolls.
 *
 * Names are intentionally always generated in **English** and are never
 * run through the i18n layer — they act as shared proper nouns across the
 * entire playerbase, so a Spanish-speaking pilot typing "estoy en Cygnus
 * Drift Prime" in chat means the exact same place to an English pilot,
 * and vice versa. Do not translate or localize the output; if you need
 * a localized surrounding string, compose it as `${t(...)} ${sectorName(c)}`.
 */
/**
 * Deterministically picks the lore-dominant race for a sector coordinate,
 * or returns `null` for "unclaimed frontier" pockets that players are
 * allowed to claim through the sovereignty system.
 *
 * The selection is driven by each civilization's
 * `CIVILIZATION_LORE[r].territoryPreferences.idealSectorDistance`: a race
 * is a candidate iff the sector's distance from origin falls inside its
 * preferred ring. Ties are broken by a stable per-coord hash.
 *
 * Invariants:
 *  - `(0,0,0)` is always Terran — new players must always land in a
 *    Terran-owned home sector regardless of other rolls.
 *  - About 10-20% of candidate sectors roll `null`, creating the pool of
 *    claimable frontier sectors. The null rate is driven by a fixed salt
 *    so it doesn't drift as the sector name hash changes.
 *  - The function is pure + deterministic, so servers and clients agree
 *    on ownership for any coord without needing to persist the full
 *    dominance map.
 */
export function dominantRace(coord) {
    if (coord.x === 0 && coord.y === 0 && coord.z === 0)
        return RaceId.Terran;
    const dist = sectorDistance(coord);
    const eligible = [];
    for (const rId of ALL_RACE_IDS) {
        const lore = CIVILIZATION_LORE[rId];
        const pref = lore.territoryPreferences.idealSectorDistance;
        if (dist >= pref.min && dist <= pref.max)
            eligible.push(rId);
    }
    // "Unclaimed frontier" roll — a small slice of otherwise eligible
    // sectors intentionally has no dominant race so players have somewhere
    // to plant their flag. Weighted so the rate stays roughly 10-20%.
    const frontierRoll = hash(MASTER_SEED ^ 0xF20817E2, coord.x, coord.y, coord.z) % 100;
    if (frontierRoll < 15)
        return null;
    if (eligible.length === 0)
        return null;
    const pickRoll = hash(MASTER_SEED ^ 0xD091011A, coord.x, coord.y, coord.z);
    return eligible[pickRoll % eligible.length];
}
export function sectorName(coord) {
    if (coord.x === 0 && coord.y === 0 && coord.z === 0) {
        return 'Origin Sanctum';
    }
    // Three decorrelated samples — we re-hash with different salts per
    // slot so "Alpha"-prefixed sectors aren't always paired with the same
    // middle word at a given y.
    const p = hash(MASTER_SEED ^ 0xA11ACE, coord.x, coord.y, coord.z);
    const m = hash(MASTER_SEED ^ 0xB0B1EE, coord.x, coord.y, coord.z);
    const s = hash(MASTER_SEED ^ 0xC0DEFE, coord.x, coord.y, coord.z);
    const pi = p % SECTOR_NAME_PREFIXES.length;
    const mi = m % SECTOR_NAME_MIDDLES.length;
    const si = s % SECTOR_NAME_SUFFIXES.length;
    return `${SECTOR_NAME_PREFIXES[pi]} ${SECTOR_NAME_MIDDLES[mi]} ${SECTOR_NAME_SUFFIXES[si]}`;
}
export function generateSector(sx, sy, sz) {
    const seed = hash(MASTER_SEED, sx, sy, sz);
    const rng = new SeededRandom(seed);
    const coord = { x: sx, y: sy, z: sz };
    const dist = sectorDistance(coord);
    const difficulty = getZoneDifficulty(coord);
    const planets = generatePlanets(rng, seed, dist);
    const stars = generateStars(rng, seed, planets, dist);
    const asteroidFields = generatePlanetAsteroidFields(rng, planets, dist);
    const station = generateStation(rng, seed, coord, planets);
    const warpGates = generateWarpGates(rng, seed, coord);
    const nebula = generateNebula(rng, seed, dist);
    const comets = generateComets(rng, seed, dist);
    const npcs = generatePlanetNPCs(rng, planets, difficulty, station, dist);
    return {
        coord, seed, stars, planets, asteroidFields, station,
        warpGates, nebula, comets, npcs, difficulty,
        // `zoneName` is now a unique procedural name (not a difficulty tier
        // label). `difficulty` is 1–3 from `getZoneDifficulty`; HUD "SAFE"
        // is only from station/warp orbits, not from this field.
        zoneName: sectorName(coord),
    };
}
const SPREAD = SECTOR_SIZE * 0.45;
/**
 * Minimum clearance (in world units) between points of interest inside a
 * single sector. These values are tuned for a ~4096 unit sector and keep
 * planets / fields / stations from clumping into a single visible group.
 * If `SECTOR_SIZE` changes significantly, scale these accordingly.
 */
const PLANET_PLANET_MIN_GAP = 500;
const PLANET_FIELD_MIN_GAP = 300;
const PLANET_STAR_MIN_GAP = 250;
const PLANET_STATION_MIN_GAP = 350;
// Uniform random direction on the unit sphere. Used so content is distributed
// through the full 3D volume of a sector rather than a flat XZ disk.
function randomUnitVector(rng) {
    const u = rng.nextFloat(-1, 1);
    const phi = rng.nextFloat(0, Math.PI * 2);
    const s = Math.sqrt(Math.max(0, 1 - u * u));
    return { x: s * Math.cos(phi), y: u, z: s * Math.sin(phi) };
}
function generateStars(rng, seed, planets, sectorDist = 0) {
    const distMul = sectorDifficultyMultiplier(sectorDist);
    // Deeper sectors are more likely to contain stars, and up to 3 of them.
    const base = sectorDist > 15 ? 1 : 0;
    const count = rng.nextInt(base, Math.min(3, 2 + Math.floor((distMul - 1))));
    const stars = [];
    for (let i = 0; i < count; i++) {
        const radius = rng.nextFloat(30, 80) * Math.min(2.0, distMul);
        const starGlow = radius * 4;
        let sx = 0, sy = 0, sz = 0;
        for (let attempt = 0; attempt < 20; attempt++) {
            sx = rng.nextFloat(-SPREAD, SPREAD);
            sy = rng.nextFloat(-SPREAD, SPREAD);
            sz = rng.nextFloat(-SPREAD, SPREAD);
            let safe = true;
            for (const p of planets) {
                const dx = sx - p.position.x;
                const dy = sy - p.position.y;
                const dz = sz - p.position.z;
                const minSep = p.atmosphereRadius + starGlow + PLANET_STAR_MIN_GAP;
                if (dx * dx + dy * dy + dz * dz < minSep * minSep) {
                    safe = false;
                    break;
                }
            }
            if (safe)
                break;
        }
        stars.push({
            position: { x: sx, y: sy, z: sz },
            radius,
            color: STAR_COLORS[rng.nextInt(0, STAR_COLORS.length - 1)],
            luminosity: rng.nextFloat(0.5, 2.0),
        });
    }
    return stars;
}
const RACE_RADIUS_SCALE = {
    [RaceId.Terran]: { min: 200, max: 500 },
    [RaceId.Zyrkath]: { min: 180, max: 450 },
    [RaceId.Luminara]: { min: 150, max: 350 },
    [RaceId.Vokren]: { min: 200, max: 480 },
    [RaceId.ObsidianCollective]: { min: 350, max: 800 },
    [RaceId.VoidWalker]: { min: 120, max: 300 },
    [RaceId.Sylphari]: { min: 300, max: 650 },
    [RaceId.KrelgHegemony]: { min: 400, max: 850 },
    [RaceId.TharixCabal]: { min: 200, max: 480 },
    [RaceId.ChronosSeers]: { min: 320, max: 700 },
    [RaceId.NovaForgers]: { min: 500, max: 950 },
    [RaceId.AbyssalChorus]: { min: 600, max: 1100 },
    // Apex races ship monumental capital-class hulls — the planet radii
    // are bigger to match the in-world reading.
    [RaceId.EidolonConclave]: { min: 450, max: 900 },
    [RaceId.AstralLeviathan]: { min: 700, max: 1300 },
    [RaceId.SingularitySovereign]: { min: 800, max: 1500 },
};
/**
 * Coarse 0–6 "power tier" bucket for a race, derived from its unlockLevel.
 * Kept as a bucket (rather than using the raw unlockLevel directly) so the
 * distance-based spawn distribution has a small, well-understood set of
 * slots and so a new race falling between two existing unlock levels can't
 * accidentally change the spawn curve of every sector.
 *
 *   Tier 0 (unlock ≤ 0):    starter races — Terran, Zyr'kath, Luminara,
 *                           Vok'ren, Obsidian Collective.
 *   Tier 1 (unlock ≤ 20):   Sylphari.
 *   Tier 2 (unlock ≤ 30):   Krelg Hegemony, Tharix Cabal.
 *   Tier 3 (unlock ≤ 50):   Chronos Seers, Void Walker.
 *   Tier 4 (unlock ≤ 90):   Nova Forgers, Abyssal Chorus.
 *   Tier 5 (unlock ≤ 199):  Eidolon Conclave.
 *   Tier 6 (unlock ≥ 200):  Astralleviathan, Singularity Sovereigns.
 */
export function raceDepthTier(raceId) {
    const unlock = RACE_INFO[raceId]?.unlockLevel ?? 0;
    if (unlock <= 0)
        return 0;
    if (unlock <= 20)
        return 1;
    if (unlock <= 30)
        return 2;
    if (unlock <= 50)
        return 3;
    if (unlock <= 90)
        return 4;
    if (unlock <= 199)
        return 5;
    return 6;
}
export const RACE_DEPTH_MAX_TIER = 6;
/**
 * Sector-distance at which each tier "peaks" in spawn probability. At the
 * origin, tier 0 dominates; around {@link RACE_DEPTH_DIST_PER_TIER} × N
 * units out, tier N is the most likely result. Probabilities still form a
 * bell curve around the peak so *any* tier can roll at *any* distance —
 * just increasingly unlikely the further you are from the tier's peak.
 */
const RACE_DEPTH_DIST_PER_TIER = 15;
/**
 * Gaussian width (in tier units) of the spawn probability curve. Bigger
 * variance → more mixing between tiers; smaller → each sector is more
 * tightly themed around a single tier. 1.5 gives the nice-feeling shape
 * described in the spawn table in the comment on
 * {@link pickRaceWithDepthBias}.
 */
const RACE_DEPTH_TIER_VARIANCE = 1.5;
/**
 * Distance-biased random race pick.
 *
 * The selection is a two-step roll:
 *   1. Pick a **tier** (0..4) from a Gaussian distribution whose mean is
 *      `sectorDist / RACE_DEPTH_DIST_PER_TIER` — i.e. deeper sectors shift
 *      the mean towards higher tiers, so stronger/rarer races become
 *      exponentially more likely the further you travel, **but never
 *      exclusively so**. Even at the galaxy's edge a low-tier race can
 *      still pop up; even at the origin an apex race can occasionally
 *      slip through (with a tiny probability).
 *   2. Pick uniformly among races belonging to the chosen tier, optionally
 *      filtered by `opts.filter`.
 *
 * Approximate tier probabilities (ignoring filters):
 *
 *   sectorDist  |  T0    T1    T2    T3    T4
 *   ────────────┼─────────────────────────────
 *   0           | 41%   33%   17%    6%   1.2%
 *   15          | 24%   30%   24%   12%    4%
 *   30          | 12%   24%   30%   24%   12%
 *   45          |  4%   12%   24%   30%   24%
 *   60+         | 1.2%   6%   17%   33%   41%
 *
 * If `opts.filter` rejects every race in the rolled tier, we fall back to
 * the nearest tier that has a legal candidate; this keeps the function
 * total even for exotic filters.
 */
export function pickRaceWithDepthBias(rng, sectorDist, opts = {}) {
    const filter = opts.filter;
    // Bucket races by tier up-front so we can both weight the tier pick and
    // fall back to a neighbouring tier if the rolled one is empty after the
    // caller's filter.
    const tierBuckets = Array.from({ length: RACE_DEPTH_MAX_TIER + 1 }, () => []);
    for (const rId of ALL_RACE_IDS) {
        if (filter && !filter(rId))
            continue;
        tierBuckets[raceDepthTier(rId)].push(rId);
    }
    const nonEmptyTiers = tierBuckets.reduce((n, bucket) => n + (bucket.length > 0 ? 1 : 0), 0);
    if (nonEmptyTiers === 0) {
        // Filter rejected literally everyone — degrade gracefully to a plain
        // uniform pick from the full race list rather than throwing.
        return rng.pick(ALL_RACE_IDS);
    }
    const targetTier = Math.max(0, sectorDist) / RACE_DEPTH_DIST_PER_TIER;
    const variance = RACE_DEPTH_TIER_VARIANCE;
    const twoVarSq = 2 * variance * variance;
    const weights = [];
    let total = 0;
    for (let t = 0; t <= RACE_DEPTH_MAX_TIER; t++) {
        if (tierBuckets[t].length === 0) {
            weights.push(0);
            continue;
        }
        const delta = t - targetTier;
        const w = Math.exp(-(delta * delta) / twoVarSq);
        weights.push(w);
        total += w;
    }
    let roll = rng.next() * total;
    let chosenTier = RACE_DEPTH_MAX_TIER;
    for (let t = 0; t <= RACE_DEPTH_MAX_TIER; t++) {
        if (weights[t] === 0)
            continue;
        roll -= weights[t];
        if (roll <= 0) {
            chosenTier = t;
            break;
        }
    }
    const bucket = tierBuckets[chosenTier];
    return rng.pick(bucket);
}
/**
 * Distance-biased race pick for an inhabited planet. Thin wrapper over
 * {@link pickRaceWithDepthBias} kept as a named helper so the call sites
 * read clearly and so future tweaks to planet-specific filtering (e.g.
 * excluding hostile-only races) have a dedicated seam.
 */
function pickRaceForPlanet(rng, sectorDist) {
    return pickRaceWithDepthBias(rng, sectorDist);
}
/** How many inhabited planets spawn in this sector. Deeper sectors get more. */
function planetCountFor(sectorDist, rng) {
    const tier = sectorDifficultyTier(sectorDist);
    // Origin sector: 1 planet. Every 2 tiers unlock +1 (max 4).
    const baseExtra = Math.floor(tier / 2);
    const roll = rng.nextInt(0, 1); // small variance
    return Math.min(4, 1 + baseExtra + (roll && sectorDist > 5 ? 1 : 0));
}
function generatePlanets(rng, seed, sectorDist) {
    const count = planetCountFor(sectorDist, rng);
    const planets = [];
    const distMul = sectorDifficultyMultiplier(sectorDist);
    const maxAllowedRadius = SECTOR_SIZE * 0.15;
    for (let i = 0; i < count; i++) {
        const raceId = pickRaceForPlanet(rng, sectorDist);
        const info = RACE_INFO[raceId];
        const radiusRange = RACE_RADIUS_SCALE[raceId] ?? { min: 200, max: 500 };
        // Deeper sectors host larger worlds. Sylphari/Krelg/Abyssal naturally
        // already scale higher thanks to their wider base ranges.
        const scaledMax = Math.min(maxAllowedRadius, radiusRange.max * Math.min(1.5, distMul));
        const scaledMin = Math.min(scaledMax, radiusRange.min * Math.min(1.2, 1 + (distMul - 1) * 0.4));
        const radius = rng.nextFloat(scaledMin, scaledMax);
        const atmosphereRadius = radius * rng.nextFloat(1.08, 1.15);
        // Place successive planets on progressively wider orbits so they end up
        // on opposite sides of the sector rather than clumped around the center.
        const minOrbit = atmosphereRadius + 400 + i * 300;
        const maxOrbit = SECTOR_SIZE * 0.5 - atmosphereRadius - 150;
        if (maxOrbit <= minOrbit)
            continue;
        const orbitRadius = rng.nextFloat(minOrbit, maxOrbit);
        let dir = randomUnitVector(rng);
        // Avoid overlapping previously-placed planets. We look for a large
        // air-gap between atmospheres so two planets are rarely in the same
        // visual range from any point in the sector.
        for (let attempt = 0; attempt < 12; attempt++) {
            const cx = dir.x * orbitRadius;
            const cy = dir.y * orbitRadius;
            const cz = dir.z * orbitRadius;
            let overlap = false;
            for (const p of planets) {
                const dx = cx - p.position.x;
                const dy = cy - p.position.y;
                const dz = cz - p.position.z;
                const minSep = p.atmosphereRadius + atmosphereRadius + PLANET_PLANET_MIN_GAP;
                if (dx * dx + dy * dy + dz * dz < minSep * minSep) {
                    overlap = true;
                    break;
                }
            }
            if (!overlap)
                break;
            dir = randomUnitVector(rng);
        }
        // Per-planet patrol size. Tuned downward from the original
        // `3 + floor(distMul × 4) + nextInt(0, 3)` so a typical mid-depth
        // planet now hosts 4-6 patrol ships instead of 7-10. Combined with
        // the tighter `min(1.6, distMul)` density cap in `generatePlanetNPCs`
        // below, the overall sector NPC budget drops by ~45% — enough room
        // for a player to meaningfully explore between fights without
        // collapsing the deeper-sector difficulty curve (which is preserved
        // by leaving the `distMul` term in place, just with a smaller
        // coefficient and a lower clamp).
        const npcCount = 2 + Math.floor(distMul * 2) + rng.nextInt(0, 2);
        planets.push({
            position: {
                x: dir.x * orbitRadius,
                y: dir.y * orbitRadius,
                z: dir.z * orbitRadius,
            },
            radius,
            atmosphereRadius,
            color: info.primaryColor,
            atmosphereColor: info.secondaryColor,
            hasRings: rng.next() > 0.6,
            orbitRadius,
            orbitSpeed: rng.nextFloat(0.001, 0.01),
            seed: hash(seed, i, 999),
            raceId,
            civilizationName: info.name,
            npcCount,
            premiumOnly: isPremiumPlanet(raceId, sectorDist),
        });
    }
    return planets;
}
function pickRaceResource(rng, raceId, sectorDist) {
    const preferred = RACE_RESOURCES[raceId];
    const available = preferred.filter(tier => sectorDist >= RESOURCE_INFO[tier].minSectorDistance);
    if (available.length > 0)
        return rng.pick(available);
    return ResourceTier.ScrapMetal;
}
function buildSizeDistribution(rng, count) {
    const sizes = [];
    for (let j = 0; j < count; j++) {
        const r = rng.next();
        if (r < 0.4)
            sizes.push(AsteroidSize.Small);
        else if (r < 0.7)
            sizes.push(AsteroidSize.Medium);
        else if (r < 0.9)
            sizes.push(AsteroidSize.Large);
        else
            sizes.push(AsteroidSize.Huge);
    }
    return sizes;
}
function generatePlanetAsteroidFields(rng, planets, sectorDist) {
    const fields = [];
    const halfSector = SPREAD;
    for (const planet of planets) {
        const fieldCount = rng.nextInt(1, 2);
        // Push planet-orbit asteroid fields further out so a planet isn't
        // wrapped in its own halo of rocks visible at the same glance.
        const orbitZone = planet.atmosphereRadius + 250 + planet.radius * 0.5;
        for (let f = 0; f < fieldCount; f++) {
            const resourceType = pickRaceResource(rng, planet.raceId, sectorDist);
            const asteroidCount = rng.nextInt(10, 35);
            const fieldRadius = rng.nextFloat(40, 120);
            const maxDist = Math.min(orbitZone * 1.8, halfSector - fieldRadius);
            const dist = rng.nextFloat(orbitZone, Math.max(orbitZone, maxDist));
            // 3D spherical orbit around the planet so fields appear above/below
            // and in front/behind it, not only on a flat ring.
            const dir = randomUnitVector(rng);
            const cx = planet.position.x + dir.x * dist;
            const cy = planet.position.y + dir.y * dist;
            const cz = planet.position.z + dir.z * dist;
            const clamp = halfSector - fieldRadius;
            fields.push({
                center: {
                    x: Math.max(-clamp, Math.min(clamp, cx)),
                    y: Math.max(-clamp, Math.min(clamp, cy)),
                    z: Math.max(-clamp, Math.min(clamp, cz)),
                },
                radius: fieldRadius,
                count: asteroidCount,
                resourceType,
                sizeDistribution: buildSizeDistribution(rng, asteroidCount),
            });
        }
    }
    // Scattered wild asteroid fields across the sector (avoid overlapping planets
    // AND each other, so fields don't merge into a single visible blob).
    // Density is deliberately sparse: with a much larger sector, most of the
    // space should feel empty so exploration has payoff.
    const distMul = sectorDifficultyMultiplier(sectorDist);
    const wildFieldCount = Math.round(rng.nextInt(2, 4) * Math.min(2.0, distMul));
    const possibleWildRes = [];
    for (const tier of ALL_RESOURCE_TIERS) {
        const info = RESOURCE_INFO[tier];
        if (sectorDist >= info.minSectorDistance) {
            // Weight spawn by rarity — common tiers appear many times, exotics rarely.
            const weight = Math.max(1, Math.round(info.rarity * 12));
            for (let w = 0; w < weight; w++)
                possibleWildRes.push(tier);
        }
    }
    if (possibleWildRes.length === 0)
        possibleWildRes.push(ResourceTier.ScrapMetal);
    const placedWildCenters = [];
    for (let w = 0; w < wildFieldCount; w++) {
        const wildCount = rng.nextInt(8, 20);
        const fieldRadius = rng.nextFloat(50, 130);
        let cx = 0, cy = 0, cz = 0;
        for (let attempt = 0; attempt < 14; attempt++) {
            cx = rng.nextFloat(-SPREAD, SPREAD);
            cy = rng.nextFloat(-SPREAD, SPREAD);
            cz = rng.nextFloat(-SPREAD, SPREAD);
            let overlaps = false;
            for (const p of planets) {
                const dx = cx - p.position.x;
                const dy = cy - p.position.y;
                const dz = cz - p.position.z;
                const minSep = p.atmosphereRadius + fieldRadius + PLANET_FIELD_MIN_GAP;
                if (dx * dx + dy * dy + dz * dz < minSep * minSep) {
                    overlaps = true;
                    break;
                }
            }
            if (!overlaps) {
                for (const prev of placedWildCenters) {
                    const dx = cx - prev.x;
                    const dy = cy - prev.y;
                    const dz = cz - prev.z;
                    const minSep = prev.r + fieldRadius + 400;
                    if (dx * dx + dy * dy + dz * dz < minSep * minSep) {
                        overlaps = true;
                        break;
                    }
                }
            }
            if (!overlaps)
                break;
        }
        placedWildCenters.push({ x: cx, y: cy, z: cz, r: fieldRadius });
        fields.push({
            center: { x: cx, y: cy, z: cz },
            radius: fieldRadius,
            count: wildCount,
            resourceType: rng.pick(possibleWildRes),
            sizeDistribution: buildSizeDistribution(rng, wildCount),
        });
    }
    return fields;
}
function generateStation(rng, _seed, coord, planets) {
    const prefix = STATION_PREFIXES[rng.nextInt(0, STATION_PREFIXES.length - 1)];
    const suffix = STATION_SUFFIXES[rng.nextInt(0, STATION_SUFFIXES.length - 1)];
    const number = rng.nextInt(1, 99);
    let sx = 0, sy = 0, sz = 0;
    for (let attempt = 0; attempt < 20; attempt++) {
        sx = rng.nextFloat(-SPREAD * 0.5, SPREAD * 0.5);
        sy = rng.nextFloat(-SPREAD * 0.5, SPREAD * 0.5);
        sz = rng.nextFloat(-SPREAD * 0.5, SPREAD * 0.5);
        let safe = true;
        for (const p of planets) {
            const dx = sx - p.position.x;
            const dy = sy - p.position.y;
            const dz = sz - p.position.z;
            const minSep = p.atmosphereRadius + PLANET_STATION_MIN_GAP;
            if (dx * dx + dy * dy + dz * dz < minSep * minSep) {
                safe = false;
                break;
            }
        }
        if (safe)
            break;
    }
    return {
        position: { x: sx, y: sy, z: sz },
        name: `${prefix}-${number} ${suffix}`,
        ownerRace: dominantRace(coord),
        size: rng.nextFloat(10, 30),
    };
}
function generateWarpGates(rng, _seed, coord) {
    // Warp gates are now sparse: only sectors matching `sectorHasWarpGate`
    // get one, so a player typically has to fly across 1-2 sector boundaries
    // before they can warp again. This forces real exploration rather than
    // teleport-hopping through every new sector.
    if (!sectorHasWarpGate(coord))
        return [];
    // Travel destinations are chosen dynamically at runtime by the player
    // from their discovered sectors; we still populate a deterministic
    // "suggested" neighbour coord as a hint for cartography tooling.
    const destOffset = {
        x: rng.nextInt(-3, 3) || 1,
        y: rng.nextInt(-1, 1),
        z: rng.nextInt(-3, 3) || 1,
    };
    return [{
            position: {
                x: rng.nextFloat(-SPREAD * 0.8, SPREAD * 0.8),
                y: rng.nextFloat(-SPREAD * 0.8, SPREAD * 0.8),
                z: rng.nextFloat(-SPREAD * 0.8, SPREAD * 0.8),
            },
            destinationSector: {
                x: coord.x + destOffset.x,
                y: coord.y + destOffset.y,
                z: coord.z + destOffset.z,
            },
        }];
}
function generateNebula(rng, seed, sectorDist) {
    if (rng.next() > 0.25)
        return null;
    const colors = [0x4422aa, 0x22aa44, 0xaa2244, 0x224488, 0x884422];
    return {
        center: {
            x: rng.nextFloat(-SPREAD, SPREAD),
            y: rng.nextFloat(-SPREAD, SPREAD),
            z: rng.nextFloat(-SPREAD, SPREAD),
        },
        radius: rng.nextFloat(100, 400),
        color: rng.pick(colors),
        density: rng.nextFloat(0.3, 0.8),
    };
}
/**
 * Mothership ship-class string. **Intentionally not part of the
 * procedural NPC class pool** — motherships have their own dedicated
 * spawn path (the gated block at the bottom of {@link generatePlanetNPCs}
 * plus the post-kill respawn queue and the `mothership_alert` event).
 * Putting `mothership` in the regular class pool would mean every
 * tier-3+ planet patrol could roll a 15k-HP capital boss, which is
 * obviously wrong.
 */
export const MOTHERSHIP_CLASS = 'mothership';
const STATION_EXCLUSION_RADIUS = 200;
const PLANET_CLASS_WEIGHTS_BY_TIER = [
    // Tier 0 — origin & inner ring: light defenders only. Probes show up
    // as "civilian sensor traffic", and gunboats fill the local-patrol
    // lane between scout and fighter.
    { scout: 40, fighter: 28, interceptor: 12, gunboat: 10, miner: 5, probe: 5 },
    // Tier 1 — first contested band: corvettes + support craft start
    // appearing; system patrol ranks fill out.
    { scout: 22, fighter: 22, interceptor: 16, gunboat: 10, corvette: 14, support: 6, miner: 8, probe: 2 },
    // Tier 2 — regional patrols: frigates, gunships, support craft, the
    // first assault bruisers. Cruisers still rare.
    { scout: 14, fighter: 18, interceptor: 14, corvette: 16, gunship: 10, frigate: 12, assault: 6, support: 5, miner: 5 },
    // Tier 3 — capital-leaning patrols: destroyers, cruisers, vanguards,
    // bombers. Drone-carriers occasionally lead a wing.
    { scout: 8, fighter: 14, interceptor: 12, corvette: 14, gunship: 8, frigate: 14, assault: 6, bomber: 6, vanguard: 6, destroyer: 5, cruiser: 4, drone_carrier: 3 },
    // Tier 4+ — apex patrols: full capital roster + dreadnought as a
    // rare admiral flagship. Civilian roles thin out.
    { scout: 4, fighter: 9, interceptor: 10, corvette: 10, gunship: 6, frigate: 12, assault: 6, bomber: 7, vanguard: 8, destroyer: 10, cruiser: 8, battleship: 6, carrier: 3, drone_carrier: 4, dreadnought: 2, miner: 2 },
];
const ROGUE_CLASS_WEIGHTS_BY_TIER = [
    // Tier 0 — petty pirates only; nothing heavier than a gunboat.
    { scout: 50, fighter: 30, interceptor: 10, gunboat: 10 },
    // Tier 1 — corvette raiders + assault breachers join the wolfpack.
    { scout: 26, fighter: 26, interceptor: 20, gunboat: 10, corvette: 12, assault: 6 },
    // Tier 2 — frigate pirates appear, bombers / stealth occasionally.
    { scout: 14, fighter: 22, interceptor: 20, gunboat: 6, corvette: 16, assault: 8, gunship: 6, frigate: 10, bomber: 4, stealth: 3, destroyer: 3 },
    // Tier 3 — pirate fleets with destroyers, vanguards, stealth raiders.
    { scout: 8, fighter: 14, interceptor: 16, corvette: 14, assault: 8, gunship: 6, frigate: 14, bomber: 6, vanguard: 8, stealth: 6, destroyer: 10, cruiser: 6 },
    // Tier 4+ — apex pirate kingpins, dreadnoughts as rare flagships.
    { scout: 4, fighter: 10, interceptor: 12, corvette: 10, assault: 6, gunship: 5, frigate: 13, bomber: 7, vanguard: 10, stealth: 8, destroyer: 12, cruiser: 9, battleship: 6, carrier: 2, drone_carrier: 4, dreadnought: 2 },
];
/**
 * Pick a ship class for a procedural NPC spawn. The picker is a
 * weighted random over {@link PLANET_CLASS_WEIGHTS_BY_TIER} or
 * {@link ROGUE_CLASS_WEIGHTS_BY_TIER} depending on `role`. Ties
 * defer to the table's iteration order (insertion order); since the
 * weights are small constants the visible distribution is what matters.
 *
 * Tiers above the table length collapse to the last row, which is the
 * intended "deep-sector" pool — this is what makes a sector at distance
 * 100+ keep rolling the apex hulls.
 */
function pickProcGenShipClass(rng, tier, role) {
    const table = role === 'planet'
        ? PLANET_CLASS_WEIGHTS_BY_TIER
        : ROGUE_CLASS_WEIGHTS_BY_TIER;
    const idx = Math.min(table.length - 1, Math.max(0, tier));
    const weights = table[idx];
    const entries = Object.entries(weights);
    let total = 0;
    for (const [, w] of entries)
        total += w;
    if (total <= 0)
        return 'fighter'; // defensive: never return undefined
    let roll = rng.next() * total;
    for (const [cls, w] of entries) {
        roll -= w;
        if (roll <= 0)
            return cls;
    }
    return entries[entries.length - 1][0];
}
// `unknown` maps to HOSTILE (0), not neutral (1). The lore-text for
// every race we currently tag as `unknown` describes a predatory or
// dangerous civilisation (Void Walkers, Abyssal Chorus deep-void
// leviathans, Astralleviathans, …). Treating "we don't know what they
// want" as "they're peaceful merchants" was producing the bug a player
// reported as "I was right in front of a level-12 Abyssal Chorus
// Destroyer and it just sat there until I shot it" — neutrals get NO
// weapon assigned in `populateSector` and only ever transition to the
// `flee` state, so a giant warship wearing no guns looked like a
// floating statue.
//
// Friendly / neutral races (Sylphari, Terran, Vok'ren, …) keep their
// merchant behaviour. `unknown` is now reserved for "menacing apex
// race that engages on sight" — same combat treatment as any race
// flagged hostile in the lore table.
const DISPOSITION_MAP = {
    hostile: 0,
    neutral: 1,
    friendly: 2,
    unknown: 0,
};
function npcLevel(rng, sectorDist) {
    // Origin sector (0,0,0) is the starter zone: every player spawns
    // here for the first time piloting a level-1 scout with the basic
    // pulse blaster (~3 dmg). Putting level-2 or level-3 NPCs in this
    // sector — which the legacy `base + rng.nextInt(0, 2)` formula
    // happily did — meant a brand-new pilot's very first fight could
    // be against an NPC with 30-40 % more HP and shield, which a lot
    // of testers reported as "I died on my first dogfight, the game
    // is too hard". Lock the origin to level 1 mooks only so the
    // first kill is achievable for everyone.
    if (sectorDist === 0)
        return 1;
    const tier = sectorDifficultyTier(sectorDist);
    const base = 1 + tier * 2;
    // Tier-0 buffer (sectors at distance 1-2): tighten the bonus to
    // 0-1 so the ramp from "starter sector" to "first contested
    // boundary" is gradual rather than jumping straight to a level-3
    // NPC the moment the player crosses ANY sector edge. Beyond
    // tier 0 the ± variance opens back up so deeper sectors keep
    // their mix of grunts and leaders.
    const bonus = tier === 0
        ? rng.nextInt(0, 1)
        : rng.nextInt(0, Math.max(2, tier + 1));
    return Math.max(1, base + bonus);
}
function generatePlanetNPCs(rng, planets, difficulty, station, sectorDist = 0) {
    const npcs = [];
    const distMul = sectorDifficultyMultiplier(sectorDist);
    const tier = sectorDifficultyTier(sectorDist);
    for (let pi = 0; pi < planets.length; pi++) {
        const planet = planets[pi];
        const raceInfo = RACE_INFO[planet.raceId];
        const disposition = DISPOSITION_MAP[raceInfo.disposition] ?? 1;
        // Deeper sectors → denser planet patrols, but with a tighter ceiling
        // than before (was `min(2.5, distMul)`) so a single deep planet
        // can't crowd the local sky with 30+ ships. The trend remains
        // monotone (`distMul` still scales linearly until it caps), it just
        // tops out sooner so exploration stays readable in late-game space.
        const npcCount = Math.round(planet.npcCount * Math.min(1.6, distMul));
        const orbitZone = planet.atmosphereRadius + 80 + planet.radius * 0.5;
        for (let n = 0; n < npcCount; n++) {
            const dist = rng.nextFloat(orbitZone, orbitZone * 1.8);
            const dir = randomUnitVector(rng);
            const pos = {
                x: planet.position.x + dir.x * dist,
                y: planet.position.y + dir.y * dist,
                z: planet.position.z + dir.z * dist,
            };
            const sdx = pos.x - station.position.x;
            const sdy = pos.y - station.position.y;
            const sdz = pos.z - station.position.z;
            if (Math.sqrt(sdx * sdx + sdy * sdy + sdz * sdz) < STATION_EXCLUSION_RADIUS && disposition === 0) {
                continue;
            }
            npcs.push({
                position: pos,
                disposition,
                level: npcLevel(rng, sectorDist),
                shipClass: pickProcGenShipClass(rng, tier, 'planet'),
                patrolRadius: rng.nextFloat(orbitZone * 0.3, orbitZone * 0.8),
                raceId: planet.raceId,
                planetIndex: pi,
            });
        }
    }
    // Rogue hostiles (space pirates) — more frequent the further out we go.
    // Counts trimmed from the previous (3-6 / 5-10 / 8-14) bands so a
    // player crossing into a Hard sector isn't met by a wall of 30+
    // pirates the moment they enter. The `min(1.6, distMul)` cap matches
    // the planet-NPC density ceiling so both populations scale in lock-
    // step and the global sector cap stays in a sane range.
    const rogueBase = difficulty === 1 ? rng.nextInt(2, 4)
        : difficulty === 2 ? rng.nextInt(3, 7)
            : rng.nextInt(5, 9);
    const rogueCount = Math.round(rogueBase * Math.min(1.6, distMul));
    for (let r = 0; r < rogueCount; r++) {
        const pos = {
            x: rng.nextFloat(-SPREAD, SPREAD),
            y: rng.nextFloat(-SPREAD, SPREAD),
            z: rng.nextFloat(-SPREAD, SPREAD),
        };
        const sdx = pos.x - station.position.x;
        const sdy = pos.y - station.position.y;
        const sdz = pos.z - station.position.z;
        if (Math.sqrt(sdx * sdx + sdy * sdy + sdz * sdz) < STATION_EXCLUSION_RADIUS) {
            continue;
        }
        // Depth-biased rogue race pick. Near the core the pool is dominated
        // by classic pirate races (Zyr'kath and friends); the further out the
        // sector sits, the more likely we roll a heavy-hitter frontier race
        // (Krelg, Nova, Abyssal) — but even at max depth a low-tier raider
        // can still roll, so player dogfights never become same-y.
        //
        // We filter to hostile/unknown-disposition races so "pirate Luminara"
        // doesn't appear; low-tier peaceful races (Terran/Luminara/Vok'ren)
        // aren't thematic as space-pirates anyway.
        const roguePick = pickRaceWithDepthBias(rng, sectorDist, {
            filter: (rId) => {
                const d = RACE_INFO[rId].disposition;
                return d === 'hostile' || d === 'unknown';
            },
        });
        npcs.push({
            position: pos,
            disposition: 0,
            level: npcLevel(rng, sectorDist),
            shipClass: pickProcGenShipClass(rng, tier, 'rogue'),
            patrolRadius: rng.nextFloat(80, 200),
            raceId: roguePick,
            planetIndex: 0,
        });
    }
    // Motherships are intentionally NOT generated by procgen anymore. The
    // capital-class boss spawn is event-driven (see the `mothership_alert`
    // handler in `server/src/events/event-scheduler.ts`, which now both
    // spawns the boss into a deep sector and broadcasts the announcement).
    // Keeping the spawn out of procgen makes the boss appearance feel
    // intentional / scheduled rather than something a player might walk
    // into by accident, and lets admins tune the cadence from the events
    // collection without changing world generation.
    return npcs;
}
function generateComets(rng, seed, sectorDist) {
    const comets = [];
    const distMul = sectorDifficultyMultiplier(sectorDist);
    const count = rng.nextInt(0, Math.min(5, 3 + Math.floor(distMul - 1)));
    for (let i = 0; i < count; i++) {
        // Comets carry any tier already unlocked by this sector's distance, weighted
        // by rarity so exotics remain a true reward for deep-space exploration.
        const possibleResources = [];
        for (const tier of ALL_RESOURCE_TIERS) {
            const info = RESOURCE_INFO[tier];
            if (sectorDist >= info.minSectorDistance) {
                const weight = Math.max(1, Math.round(info.rarity * 10));
                for (let w = 0; w < weight; w++)
                    possibleResources.push(tier);
            }
        }
        if (possibleResources.length === 0)
            possibleResources.push(ResourceTier.ScrapMetal);
        comets.push({
            orbitCenter: {
                x: rng.nextFloat(-SPREAD, SPREAD),
                y: rng.nextFloat(-SPREAD, SPREAD),
                z: rng.nextFloat(-SPREAD, SPREAD),
            },
            orbitRadius: rng.nextFloat(100, 500),
            orbitSpeed: rng.nextFloat(0.02, 0.08),
            orbitPhase: rng.nextFloat(0, Math.PI * 2),
            resourceType: rng.pick(possibleResources),
            size: rng.nextFloat(2, 6) * Math.min(1.6, distMul),
        });
    }
    return comets;
}
//# sourceMappingURL=sector.js.map